import { getTeamPath } from "@/utils/team.utils";
import { SPORTS_OPTIONS } from "@/utils/game.utils";
import {
  formatBrDateTime,
  formatBrShortDateYmd,
  splitBrEventDate,
  BR_TIMEZONE,
} from "@/utils/br-datetime";

export const TRAINING_COVER_PLACEHOLDER =
  "https://images.unsplash.com/photo-1517649763962-0c62306601b7?q=80&w=1600&auto=format&fit=crop";

export const TRAINING_EVENT_BADGE = {
  label: "Evento",
  icon: "celebration",
  className:
    "bg-gradient-to-r from-emerald-500 to-teal-500 text-white ring-1 ring-emerald-200/60 shadow-sm shadow-emerald-500/30",
} as const;

export const TRAINING_CT_BADGE = {
  label: "CT",
  icon: "school",
  className:
    "bg-gradient-to-r from-sky-500 to-blue-600 text-white ring-1 ring-sky-200/60 shadow-sm shadow-sky-500/30",
} as const;

export type TrainingEntityBadgeVariant = "event" | "class";

export type TrainingCoverTeam = {
  photo?: string | null;
  coverPhoto?: string | null;
};

export function resolveTrainingCoverImage(
  ...sources: Array<string | null | undefined>
): string {
  for (const source of sources) {
    const trimmed = source?.trim();
    if (trimmed) return trimmed;
  }
  return TRAINING_COVER_PLACEHOLDER;
}

export function getTrainingClassCoverImage(trainingClass: {
  coverImage?: string | null;
  team?: TrainingCoverTeam | null;
}): string {
  return resolveTrainingCoverImage(
    trainingClass.coverImage,
    trainingClass.team?.coverPhoto,
    trainingClass.team?.photo,
  );
}

export function getTrainingSessionCoverImage(session: {
  coverImage?: string | null;
  team?: TrainingCoverTeam | null;
}): string {
  return resolveTrainingCoverImage(
    session.coverImage,
    session.team?.coverPhoto,
    session.team?.photo,
  );
}

export type TrainingSessionRef =
  | string
  | {
      id: string;
      shortCode?: string | null;
    };

export function getTrainingSessionPath(
  ref: TrainingSessionRef,
  suffix = "",
): string {
  const sub = suffix ? `/${suffix.replace(/^\//, "")}` : "";
  if (typeof ref === "object" && ref.shortCode) {
    return `/tr/${ref.shortCode}${sub}`;
  }
  const id = typeof ref === "string" ? ref : ref.id;
  return `/events/${id}${sub}`;
}

export function getTeamTrainingPath(teamId: string, suffix = ""): string {
  const base = `${getTeamPath(teamId, "training")}`;
  return suffix ? `${base}/${suffix.replace(/^\//, "")}` : base;
}

export function getTeamEventsPath(teamId: string, suffix = ""): string {
  const base = getTeamPath(teamId, "events");
  return suffix ? `${base}/${suffix.replace(/^\//, "")}` : base;
}

export function getTeamTrainingPublicPath(teamId: string): string {
  return getTeamPath(teamId, "training/public");
}

export function getTeamTrainingScheduleManagePath(
  teamId: string,
  scheduleId: string,
): string {
  return getTeamTrainingPath(teamId, `manage/${scheduleId}`);
}

export function getTrainingClassPath(
  teamId: string,
  classId: string,
  suffix = "",
): string {
  const base = `${getTeamPath(teamId, "training")}/${classId}`;
  return suffix ? `${base}/${suffix.replace(/^\//, "")}` : base;
}

export function formatTrainingDate(iso: string) {
  return formatBrDateTime(iso);
}

export function splitTrainingEventDate(iso: string | null | undefined) {
  return splitBrEventDate(iso);
}

export function getTrainingSportLabel(sport?: string | null) {
  if (!sport) return null;
  return SPORTS_OPTIONS.find((s) => s.value === sport)?.label ?? sport;
}

export function formatTrainingPeriodLabel(
  open: string | null | undefined,
  close: string | null | undefined,
) {
  if (!open && !close) return null;
  const fmt = (s: string) =>
    new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: BR_TIMEZONE,
    }).format(new Date(s));
  try {
    if (open && close) return `${fmt(open)} — ${fmt(close)}`;
    if (open) return `A partir de ${fmt(open)}`;
    if (close) return `Até ${fmt(close)}`;
  } catch {
    return null;
  }
  return null;
}

export type TrainingRegistrationWindowStatus = "open" | "upcoming" | "closed" | "unscheduled";

export function getTrainingRegistrationWindowStatus(
  openAt?: string | null,
  closeAt?: string | null,
): TrainingRegistrationWindowStatus {
  const now = Date.now();
  if (!openAt && !closeAt) return "unscheduled";
  if (openAt && new Date(openAt).getTime() > now) return "upcoming";
  if (closeAt && new Date(closeAt).getTime() < now) return "closed";
  return "open";
}

export function formatBrl(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

export function trainingAccessLabel(accessType: string, price: number): string {
  if (accessType === "VIP_FREE") return "Incluso no VIP";
  if (price <= 0) return "Gratuito";
  return `R$ ${price.toFixed(2).replace(".", ",")}`;
}

export function trainingStatusLabel(status: string): string {
  const map: Record<string, string> = {
    DRAFT: "Rascunho",
    OPEN: "Aberta",
    FULL: "Lotada",
    COMPLETED: "Concluída",
    CANCELLED: "Cancelada",
    PENDING_PAYMENT: "Aguardando pagamento",
    CONFIRMED: "Confirmada",
    ATTENDED: "Presente",
    WAITING: "Lista de espera",
  };
  return map[status] ?? status;
}

export const TRAINING_WEEKDAYS = [
  { value: "MON", label: "Seg" },
  { value: "TUE", label: "Ter" },
  { value: "WED", label: "Qua" },
  { value: "THU", label: "Qui" },
  { value: "FRI", label: "Sex" },
  { value: "SAT", label: "Sáb" },
  { value: "SUN", label: "Dom" },
] as const;

export type TrainingWeekday = (typeof TRAINING_WEEKDAYS)[number]["value"];

const WEEKDAY_LABEL: Record<string, string> = Object.fromEntries(
  TRAINING_WEEKDAYS.map((d) => [d.value, d.label]),
);

export function formatTrainingScheduleDays(days: string[] | null | undefined): string {
  if (!days?.length) return "—";
  const order = TRAINING_WEEKDAYS.map((d) => d.value);
  return [...days]
    .sort((a, b) => order.indexOf(a as TrainingWeekday) - order.indexOf(b as TrainingWeekday))
    .map((d) => WEEKDAY_LABEL[d] ?? d)
    .join(", ");
}

export function formatTrainingScheduleSummary(
  days: string[] | null | undefined,
  time?: string | null,
  startsOn?: string | null,
): string {
  const daysLabel = formatTrainingScheduleDays(days);
  const timeLabel = time?.trim() ? time.trim() : "";
  const startLabel = startsOn?.trim() ? formatBrShortDateYmd(startsOn) : "";

  const scheduleParts = [startLabel ? `desde ${startLabel}` : "", daysLabel, timeLabel].filter(
    Boolean,
  );
  return scheduleParts.join(" · ");
}

/** Texto legível da recorrência para formulários e confirmações. */
export function formatTrainingRecurrencePreview(
  days: string[] | null | undefined,
  time?: string | null,
  startsOn?: string | null,
): string | null {
  if (!days?.length) return null;
  const daysLabel = formatTrainingScheduleDays(days);
  const parts = ["Toda semana", daysLabel];
  if (time?.trim()) parts.push(`às ${time.trim()}`);
  if (startsOn?.trim()) parts.push(`a partir de ${formatBrShortDateYmd(startsOn)}`);
  return parts.join(" · ");
}

export const TRAINING_LEVEL_OPTIONS = [
  { value: "INICIANTE", label: "Iniciante" },
  { value: "INTERMEDIARIO", label: "Intermediário" },
  { value: "AVANCADO", label: "Avançado" },
] as const;

export function trainingLevelLabel(level?: string | null): string {
  if (!level) return "—";
  return TRAINING_LEVEL_OPTIONS.find((o) => o.value === level)?.label ?? level;
}

export function trainingPricingTypeLabel(pricingType: string): string {
  if (pricingType === "VIP_INCLUDED") return "Incluso no VIP";
  return "Mensalidade";
}

export function buildTrainingPaymentDescription(enrollment: {
  trainingClass?: { name?: string | null } | null;
  session?: { title?: string | null } | null;
}): string {
  const label = enrollment.trainingClass?.name ?? enrollment.session?.title ?? "Inscrição";
  return enrollment.trainingClass ? `CT: ${label}` : `Evento: ${label}`;
}
